/**
 * MasiniTab - Componenta pentru tab-ul Autovehicule
 * Extrasă din AdminDashboard.jsx pentru modularizare și performanță
 */
import { memo, useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { format } from 'date-fns';
import { Plus, Edit, Trash2, Calendar, FileText, Upload, Eye, Download, Search, X, Filter } from 'lucide-react';

const MasiniTab = memo(function MasiniTab({
  cars,
  newCar,
  setNewCar,
  editingCar,
  setEditingCar,
  openCarDialog,
  setOpenCarDialog,
  openEditCarDialog,
  setOpenEditCarDialog,
  openPopovers,
  setOpenPopovers,
  uploadedDocuments,
  handleCreateCar,
  handleEditCar,
  handleUpdateCar,
  handleDeleteCar,
  handleFileUpload,
  handleShowHistory,
  handleShowDocument,
  handleOpenPendingDocDialog,
  pendingDocuments,
  handleCancelPendingDocument,
  handleViewPendingDocument,
  hasPermission
}) {
  // State pentru filtrare
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('toate');
  const [centralizatorFilter, setCentralizatorFilter] = useState('toate');

  // Filtrare mașini
  const filteredCars = useMemo(() => {
    return cars.filter(car => {
      // Filtrare text (nume sau număr înmatriculare)
      const matchesText = searchText === '' || 
        car.name?.toLowerCase().includes(searchText.toLowerCase()) ||
        car.license_plate?.toLowerCase().includes(searchText.toLowerCase());
      
      // Filtrare status
      const matchesStatus = statusFilter === 'toate' || car.status === statusFilter;
      
      // Filtrare centralizator
      const matchesCentralizator = centralizatorFilter === 'toate' || 
        (car.centralizator_status || 'activ') === centralizatorFilter;
      
      return matchesText && matchesStatus && matchesCentralizator;
    });
  }, [cars, searchText, statusFilter, centralizatorFilter]);

  // Reset filtre
  const resetFilters = () => {
    setSearchText('');
    setStatusFilter('toate');
    setCentralizatorFilter('toate');
  };

  const hasActiveFilters = searchText !== '' || statusFilter !== 'toate' || centralizatorFilter !== 'toate';

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Autovehicule Flotă</CardTitle>
              <CardDescription>Administrează vehiculele din flotă</CardDescription>
            </div>
            {hasPermission('masini.editare') && (
              <Dialog open={openCarDialog} onOpenChange={setOpenCarDialog}>
                <DialogTrigger asChild>
                  <Button data-testid="add-car-button" className="flex items-center gap-2">
                    <Plus className="w-4 h-4" />
                    Adaugă Mașină
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Adaugă Mașină Nouă</DialogTitle>
                    <DialogDescription>Enter the car details below</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 pt-4 max-h-[600px] overflow-y-auto">
                    <div>
                      <Label htmlFor="car-name">Nume Mașină</Label>
                      <Input
                        id="car-name"
                        data-testid="car-name-input"
                        placeholder="e.g., Toyota Camry"
                        value={newCar.name}
                        onChange={(e) => setNewCar({ ...newCar, name: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="vin">VIN (Serie Șasiu)</Label>
                      <Input
                        id="vin"
                        data-testid="vin-input"
                        placeholder="Ex: WVWZZZ3CZWE123456"
                        value={newCar.vin || ''}
                        onChange={(e) => setNewCar({ ...newCar, vin: e.target.value.toUpperCase() })}
                        maxLength={17}
                        className="font-mono uppercase"
                      />
                    </div>
                    <div>
                      <Label htmlFor="license-plate">Număr Înmatriculare</Label>
                      <Input
                        id="license-plate"
                        data-testid="license-plate-input"
                        placeholder="e.g., ABC-1234"
                        value={newCar.license_plate}
                        onChange={(e) => setNewCar({ ...newCar, license_plate: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="fuel-capacity">Capacitate Rezervor (Litri) *</Label>
                      <Input
                        id="fuel-capacity"
                        data-testid="fuel-capacity-input"
                        type="number"
                        step="0.1"
                        placeholder="Ex: 50"
                        value={newCar.fuel_capacity}
                        onChange={(e) => setNewCar({ ...newCar, fuel_capacity: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="data-achizitiei">Data Achiziției</Label>
                      <Input
                        id="data-achizitiei"
                        data-testid="data-achizitiei-input"
                        type="date"
                        value={newCar.data_achizitiei || ''}
                        onChange={(e) => setNewCar({ ...newCar, data_achizitiei: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="insurance-expiry">Data Expirare Asigurare</Label>
                      <Input
                        id="insurance-expiry"
                        data-testid="insurance-expiry-input"
                        type="date"
                        value={newCar.insurance_expiry || ''}
                        onChange={(e) => setNewCar({ ...newCar, insurance_expiry: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="tax-road-expiry">Data Expirare Taxă Drum</Label>
                      <Input
                        id="tax-road-expiry"
                        data-testid="tax-road-expiry-input"
                        type="date"
                        value={newCar.tax_road_expiry || ''}
                        onChange={(e) => setNewCar({ ...newCar, tax_road_expiry: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="inspection-expiry">Data Expirare ITP</Label>
                      <Input
                        id="inspection-expiry"
                        data-testid="inspection-expiry-input"
                        type="date"
                        value={newCar.inspection_expiry || ''}
                        onChange={(e) => setNewCar({ ...newCar, inspection_expiry: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="casco-expiry">Data Expirare CASCO</Label>
                      <Input
                        id="casco-expiry"
                        data-testid="casco-expiry-input"
                        type="date"
                        value={newCar.casco_expiry || ''}
                        onChange={(e) => setNewCar({ ...newCar, casco_expiry: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="copie-conforma-expiry">Data Expirare Copie Conformă</Label>
                      <Input
                        id="copie-conforma-expiry"
                        data-testid="copie-conforma-expiry-input"
                        type="date"
                        value={newCar.copie_conforma_expiry || ''}
                        onChange={(e) => setNewCar({ ...newCar, copie_conforma_expiry: e.target.value })}
                      />
                    </div>
                    <Button onClick={handleCreateCar} data-testid="submit-car-button" className="w-full">
                      Adaugă Mașină
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
            
            {/* Edit Car Dialog */}
            {hasPermission('masini.editare') && (
              <Dialog open={openEditCarDialog} onOpenChange={setOpenEditCarDialog}>
                <DialogContent className="max-w-4xl">
                  <DialogHeader>
                    <DialogTitle>Editează Mașină</DialogTitle>
                    <DialogDescription>Update car details and expiry dates</DialogDescription>
                  </DialogHeader>
                  {editingCar && (
                    <div className="space-y-4 pt-4 max-h-[70vh] overflow-y-auto">
                      <div>
                        <Label htmlFor="edit-car-name" className="flex items-center gap-2">
                          <span className="text-lg">🚗</span>
                          Nume Mașină
                        </Label>
                        <Input
                          id="edit-car-name"
                          data-testid="edit-car-name-input"
                          value={editingCar.name}
                          onChange={(e) => setEditingCar({ ...editingCar, name: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit-vin" className="flex items-center gap-2">
                          <span className="text-lg">🔢</span>
                          VIN (Serie Șasiu)
                        </Label>
                        <Input
                          id="edit-vin"
                          data-testid="edit-vin-input"
                          value={editingCar.vin || ''}
                          onChange={(e) => setEditingCar({ ...editingCar, vin: e.target.value.toUpperCase() })}
                          placeholder="Ex: WVWZZZ3CZWE123456"
                          maxLength={17}
                          className="font-mono uppercase"
                        />
                        <p className="text-xs text-slate-500 mt-1">17 caractere alfanumerice</p>
                      </div>
                      <div>
                        <Label htmlFor="edit-license-plate" className="flex items-center gap-2">
                          <span className="inline-flex items-center px-1.5 py-0.5 border-2 border-slate-800 rounded text-[10px] font-bold bg-white">AB-01-CD</span>
                          Număr Înmatriculare
                        </Label>
                        <Input
                          id="edit-license-plate"
                          data-testid="edit-license-plate-input"
                          value={editingCar.license_plate}
                          onChange={(e) => setEditingCar({ ...editingCar, license_plate: e.target.value })}
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="edit-fuel-capacity" className="flex items-center gap-2">
                          <span className="text-lg">⛽</span>
                          Capacitate Rezervor (Litri)
                        </Label>
                        <Input
                          id="edit-fuel-capacity"
                          data-testid="edit-fuel-capacity-input"
                          type="number"
                          step="0.1"
                          placeholder="Ex: 50"
                          value={editingCar.fuel_capacity || ''}
                          onChange={(e) => setEditingCar({ ...editingCar, fuel_capacity: e.target.value })}
                        />
                      </div>

                      <div>
                        <Label htmlFor="edit-fuel-type" className="flex items-center gap-2">
                          <span className="text-lg">🛢️</span>
                          Tip Combustibil
                        </Label>
                        <Select
                          value={editingCar.fuel_type || ''}
                          onValueChange={(value) => setEditingCar({ ...editingCar, fuel_type: value })}
                        >
                          <SelectTrigger data-testid="edit-fuel-type-select">
                            <SelectValue placeholder="Selectează tipul de combustibil" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Motorina">Motorină</SelectItem>
                            <SelectItem value="Benzina">Benzină</SelectItem>
                            <SelectItem value="GPL">GPL</SelectItem>
                            <SelectItem value="Electric">Electric</SelectItem>
                            <SelectItem value="Hibrid">Hibrid</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div>
                        <Label htmlFor="edit-consum-normat" className="flex items-center gap-2">
                          <span className="text-lg">📊</span>
                          Consum Normat (L/100km)
                        </Label>
                        <Input
                          id="edit-consum-normat"
                          data-testid="edit-consum-normat-input"
                          type="number"
                          step="0.1"
                          placeholder="Ex: 8.5"
                          value={editingCar.consum_normat || ''}
                          onChange={(e) => setEditingCar({ ...editingCar, consum_normat: e.target.value })}
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="edit-status" className="flex items-center gap-2">
                          <span className="text-lg">✅</span>
                          Status
                        </Label>
                        <Select 
                          value={editingCar.status} 
                          onValueChange={(value) => setEditingCar({ ...editingCar, status: value })}
                        >
                          <SelectTrigger data-testid="edit-status-select">
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="available">Available</SelectItem>
                            <SelectItem value="in_use">In Use</SelectItem>
                            <SelectItem value="nonavailable">Nonavailable</SelectItem>
                            <SelectItem value="damaged">Damaged</SelectItem>
                            <SelectItem value="engine_problem">Engine Problem</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Vizibilitate Centralizator */}
                      <div>
                        <Label htmlFor="edit-centralizator-status" className="flex items-center gap-2">
                          <span className="text-lg">📊</span>
                          Vizibilitate Centralizator
                        </Label>
                        <Select 
                          value={editingCar.centralizator_status || 'activ'} 
                          onValueChange={(value) => setEditingCar({ ...editingCar, centralizator_status: value })}
                        >
                          <SelectTrigger data-testid="edit-centralizator-status-select">
                            <SelectValue placeholder="Selectează" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="activ">
                              <span className="flex items-center gap-2">
                                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                                Activ (circulă normal)
                              </span>
                            </SelectItem>
                            <SelectItem value="necirculat">
                              <span className="flex items-center gap-2">
                                <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
                                Necirculat (staționează)
                              </span>
                            </SelectItem>
                            <SelectItem value="exclus">
                              <span className="flex items-center gap-2">
                                <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                                Exclus (nu apare în centralizator)
                              </span>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-slate-500 mt-1">
                          Activ = foi de parcurs cu km | Necirculat = apare ca "Necirculat" | Exclus = ascuns complet
                        </p>
                      </div>
                      
                      {/* Data achiziției - câmp separat cu upload document */}
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <span className="text-lg">📅</span>
                          Data Achiziției
                        </Label>
                        <Input
                          type="date"
                          value={editingCar.data_achizitiei || ''}
                          onChange={(e) => setEditingCar({ ...editingCar, data_achizitiei: e.target.value })}
                          className="w-full"
                        />
                        <div className="mt-2">
                          <Label className="text-xs text-slate-500 flex items-center gap-1">
                            <span>📄</span>
                            Document Achiziție (PDF, imagine)
                          </Label>
                          <Input
                            type="file"
                            accept="image/*,.pdf"
                            onChange={(e) => handleFileUpload('document_achizitie', e)}
                            className="text-sm mt-1"
                          />
                          {(uploadedDocuments.document_achizitie || editingCar.document_achizitie) && (
                            <p className="text-xs text-green-600 mt-1">✓ Document atașat</p>
                          )}
                        </div>
                      </div>
                      
                      {/* CIV - Carte Identitate Vehicul (Față + Verso) */}
                      <div className="space-y-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <Label className="text-blue-800 font-medium flex items-center gap-2">
                          <span className="text-lg">🪪</span>
                          CIV - Carte Identitate Vehicul
                        </Label>
                        
                        {/* CIV Față */}
                        <div className="space-y-1">
                          <Label className="text-xs text-blue-600 flex items-center gap-1">
                            <span>🪪</span>
                            Față
                          </Label>
                          <Input
                            type="file"
                            accept="image/*,.pdf"
                            onChange={(e) => handleFileUpload('document_civ', e)}
                            className="text-sm bg-white"
                          />
                          {(uploadedDocuments.document_civ || editingCar.document_civ) && (
                            <p className="text-xs text-green-600">✓ Față atașată</p>
                          )}
                        </div>
                        
                        {/* CIV Verso */}
                        <div className="space-y-1">
                          <Label className="text-xs text-purple-600 flex items-center gap-1">
                            <span>📋</span>
                            Verso
                          </Label>
                          <Input
                            type="file"
                            accept="image/*,.pdf"
                            onChange={(e) => handleFileUpload('document_civ_copie', e)}
                            className="text-sm bg-white"
                          />
                          {(uploadedDocuments.document_civ_copie || editingCar.document_civ_copie) && (
                            <p className="text-xs text-green-600">✓ Verso atașat</p>
                          )}
                        </div>
                      </div>
                      
                      {/* Date pickers with file upload for each expiry */}
                      {[
                        { key: 'insurance_expiry', label: 'Asigurare RCA', icon: '🛡️' },
                        { key: 'tax_road_expiry', label: 'Taxă Drum (Rovinietă)', icon: '🛣️' },
                        { key: 'inspection_expiry', label: 'ITP', icon: '🔧' },
                        { key: 'casco_expiry', label: 'CASCO', icon: '💎' }
                      ].map(({ key, label, icon }) => (
                        <div key={key} className="space-y-2">
                          <Label className="flex items-center gap-2">
                            <span className="text-lg">{icon}</span>
                            {label}
                          </Label>
                          <Input
                            type="date"
                            value={editingCar[key] ? (editingCar[key] instanceof Date ? editingCar[key].toISOString().split('T')[0] : new Date(editingCar[key]).toISOString().split('T')[0]) : ''}
                            onChange={(e) => setEditingCar({ ...editingCar, [key]: e.target.value ? new Date(e.target.value) : null })}
                            data-testid={`edit-${key.replace('_', '-')}-picker`}
                            className="w-full"
                          />
                          <Input
                            type="file"
                            accept="image/*,.pdf"
                            onChange={(e) => handleFileUpload(key, e)}
                            data-testid={`${key.replace('_', '-')}-document-upload`}
                            className="text-sm"
                          />
                          {uploadedDocuments[key] && (
                            <p className="text-xs text-green-600">✓ {uploadedDocuments[key].name}</p>
                          )}
                          
                          {/* Buton pentru încărcare document viitor */}
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleOpenPendingDocDialog(editingCar, key, label)}
                            className="w-full text-xs text-green-600 border-green-300 hover:bg-green-50"
                          >
                            <Calendar className="w-3 h-3 mr-1" />
                            Încarcă Document Viitor
                          </Button>
                        </div>
                      ))}
                      
                      {/* Copie Conformă - cu opțiune de dezactivare */}
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <span className="text-lg">📑</span>
                          Copie Conformă
                        </Label>
                        
                        {/* Checkbox pentru dezactivare */}
                        <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg border">
                          <input
                            type="checkbox"
                            id="copie-conforma-nenecesar"
                            checked={editingCar.copie_conforma_nenecesar || false}
                            onChange={(e) => setEditingCar({ 
                              ...editingCar, 
                              copie_conforma_nenecesar: e.target.checked,
                              copie_conforma_expiry: e.target.checked ? null : editingCar.copie_conforma_expiry
                            })}
                            className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                            data-testid="copie-conforma-nenecesar-checkbox"
                          />
                          <label htmlFor="copie-conforma-nenecesar" className="text-sm text-slate-700 cursor-pointer">
                            Nu este necesară pentru această mașină
                          </label>
                        </div>
                        
                        {editingCar.copie_conforma_nenecesar ? (
                          <div className="p-3 bg-gray-100 rounded-lg text-center text-gray-500 text-sm font-medium">
                            Nenecesar
                          </div>
                        ) : (
                          <>
                            <Input
                              type="date"
                              value={editingCar.copie_conforma_expiry ? (editingCar.copie_conforma_expiry instanceof Date ? editingCar.copie_conforma_expiry.toISOString().split('T')[0] : new Date(editingCar.copie_conforma_expiry).toISOString().split('T')[0]) : ''}
                              onChange={(e) => setEditingCar({ ...editingCar, copie_conforma_expiry: e.target.value ? new Date(e.target.value) : null })}
                              data-testid="edit-copie-conforma-expiry-picker"
                              className="w-full"
                            />
                            <Input
                              type="file"
                              accept="image/*,.pdf"
                              onChange={(e) => handleFileUpload('copie_conforma_expiry', e)}
                              data-testid="copie-conforma-expiry-document-upload"
                              className="text-sm"
                            />
                            {uploadedDocuments['copie_conforma_expiry'] && (
                              <p className="text-xs text-green-600">✓ {uploadedDocuments['copie_conforma_expiry'].name}</p>
                            )}
                            
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => handleOpenPendingDocDialog(editingCar, 'copie_conforma_expiry', 'Copie Conformă')}
                              className="w-full text-xs text-green-600 border-green-300 hover:bg-green-50"
                            >
                              <Calendar className="w-3 h-3 mr-1" />
                              Încarcă Document Viitor
                            </Button>
                          </>
                        )}
                      </div>
                      <Button onClick={handleUpdateCar} data-testid="update-car-button" className="w-full">
                        Actualizează Mașină
                      </Button>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {/* Bara de filtrare */}
          <div className="mb-4 p-4 bg-slate-50 rounded-lg border">
            <div className="flex flex-wrap items-center gap-3">
              {/* Căutare text */}
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Caută după nume sau număr..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  className="pl-9 bg-white"
                  data-testid="car-search-input"
                />
                {searchText && (
                  <button 
                    onClick={() => setSearchText('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              
              {/* Filtru Status */}
              <div className="min-w-[160px]">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger data-testid="car-status-filter" className="bg-white">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="toate">Toate statusurile</SelectItem>
                    <SelectItem value="available">Disponibilă</SelectItem>
                    <SelectItem value="in_use">În Folosință</SelectItem>
                    <SelectItem value="nonavailable">Indisponibilă</SelectItem>
                    <SelectItem value="damaged">Avariată</SelectItem>
                    <SelectItem value="engine_problem">Problemă Motor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Filtru Centralizator */}
              <div className="min-w-[160px]">
                <Select value={centralizatorFilter} onValueChange={setCentralizatorFilter}>
                  <SelectTrigger data-testid="car-centralizator-filter" className="bg-white">
                    <SelectValue placeholder="Centralizator" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="toate">Toate</SelectItem>
                    <SelectItem value="activ">Activ</SelectItem>
                    <SelectItem value="necirculat">Necirculat</SelectItem>
                    <SelectItem value="exclus">Exclus</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Buton reset filtre */}
              {hasActiveFilters && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={resetFilters}
                  className="flex items-center gap-1"
                  data-testid="car-reset-filters"
                >
                  <X className="w-4 h-4" />
                  Resetează
                </Button>
              )}
            </div>
            
            {/* Info rezultate */}
            <div className="mt-2 text-sm text-slate-500 flex items-center gap-2">
              <Filter className="w-4 h-4" />
              {hasActiveFilters ? (
                <span>
                  Afișate <strong>{filteredCars.length}</strong> din <strong>{cars.length}</strong> mașini
                </span>
              ) : (
                <span>Total: <strong>{cars.length}</strong> mașini</span>
              )}
            </div>
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nume Mașină</TableHead>
                  <TableHead>VIN</TableHead>
                  <TableHead>Număr Înmatriculare</TableHead>
                  <TableHead>Data Achiziției</TableHead>
                  <TableHead>CIV</TableHead>
                  <TableHead>Centralizator</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Asigurare</TableHead>
                  <TableHead>Taxă Drum</TableHead>
                  <TableHead>ITP</TableHead>
                  <TableHead>CASCO</TableHead>
                  <TableHead>Copie Conformă</TableHead>
                  <TableHead>Creat De</TableHead>
                  <TableHead className="text-right">Acțiuni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCars.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={14} className="text-center py-8 text-slate-500">
                      {hasActiveFilters ? (
                        <div className="flex flex-col items-center gap-2">
                          <Search className="w-8 h-8 text-slate-300" />
                          <span>Nu s-au găsit mașini cu filtrele selectate</span>
                          <Button variant="link" size="sm" onClick={resetFilters}>
                            Resetează filtrele
                          </Button>
                        </div>
                      ) : (
                        <span>Nu există mașini înregistrate</span>
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCars.map((car) => (
                  <TableRow key={car.id} data-testid={`car-row-${car.id}`}>
                    <TableCell className="font-medium">{car.name}</TableCell>
                    <TableCell className="text-xs font-mono text-slate-600">{car.vin || '-'}</TableCell>
                    <TableCell>{car.license_plate}</TableCell>
                    {/* Data Achiziției - cu document */}
                    <TableCell>
                      {car.data_achizitiei ? (
                        <span 
                          onClick={() => handleShowDocument(car, 'document_achizitie', 'Document Achiziție')}
                          className={`text-xs cursor-pointer hover:underline flex items-center gap-1 ${car.document_achizitie ? 'text-blue-600 font-medium' : 'text-slate-600'}`}
                          title={car.document_achizitie ? 'Click pentru a vedea documentul' : 'Niciun document atașat'}
                        >
                          {new Date(car.data_achizitiei).toLocaleDateString()}
                          {car.document_achizitie && <FileText className="w-3 h-3" />}
                        </span>
                      ) : '-'}
                    </TableCell>
                    {/* CIV - Carte Identitate Vehicul (Față + Verso) */}
                    <TableCell>
                      {(car.document_civ || car.document_civ_copie) ? (
                        <Popover>
                          <PopoverTrigger asChild>
                            <span className="text-xs cursor-pointer hover:underline flex items-center gap-1 text-green-600 font-medium">
                              <FileText className="w-3 h-3" />
                              CIV ({(car.document_civ ? 1 : 0) + (car.document_civ_copie ? 1 : 0)})
                            </span>
                          </PopoverTrigger>
                          <PopoverContent className="w-48 p-2">
                            <div className="space-y-1">
                              <p className="text-xs font-semibold text-slate-500 mb-2">Selectează documentul:</p>
                              {car.document_civ && (
                                <button
                                  onClick={() => handleShowDocument(car, 'document_civ', 'CIV - Față')}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left rounded-lg hover:bg-slate-100 transition-colors"
                                >
                                  <FileText className="w-4 h-4 text-blue-600" />
                                  <span>Față</span>
                                </button>
                              )}
                              {car.document_civ_copie && (
                                <button
                                  onClick={() => handleShowDocument(car, 'document_civ_copie', 'CIV - Verso')}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left rounded-lg hover:bg-slate-100 transition-colors"
                                >
                                  <Eye className="w-4 h-4 text-purple-600" />
                                  <span>Verso</span>
                                </button>
                              )}
                            </div>
                          </PopoverContent>
                        </Popover>
                      ) : (
                        <span className="text-xs text-slate-400">-</span>
                      )}
                    </TableCell>
                    {/* Coloana Centralizator */}
                    <TableCell>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          car.centralizator_status === 'exclus'
                            ? 'bg-red-100 text-red-700'
                            : car.centralizator_status === 'necirculat'
                            ? 'bg-yellow-100 text-yellow-700'
                            : 'bg-green-100 text-green-700'
                        }`}
                      >
                        {car.centralizator_status === 'exclus' 
                          ? 'Exclus' 
                          : car.centralizator_status === 'necirculat' 
                          ? 'Necirculat' 
                          : 'Activ'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                          car.status === 'available'
                            ? 'bg-green-100 text-green-700'
                            : car.status === 'in_use'
                            ? 'bg-yellow-100 text-yellow-700'
                            : car.status === 'damaged'
                            ? 'bg-orange-100 text-orange-700'
                            : car.status === 'engine_problem'
                            ? 'bg-red-100 text-red-700'
                            : car.status === 'nonavailable'
                            ? 'bg-gray-100 text-gray-700'
                            : 'bg-slate-100 text-slate-700'
                        }`}
                      >
                        {car.status === 'available' 
                          ? 'Disponibilă' 
                          : car.status === 'in_use'
                          ? 'În Folosință'
                          : car.status === 'damaged'
                          ? 'Avariată'
                          : car.status === 'engine_problem'
                          ? 'Problemă Motor'
                          : car.status === 'nonavailable'
                          ? 'Indisponibilă'
                          : car.status}
                      </span>
                    </TableCell>
                    {/* Date columns with click handlers and pending documents badges */}
                    {[
                      { key: 'insurance_expiry', label: 'Asigurare' },
                      { key: 'tax_road_expiry', label: 'Taxă Drum' },
                      { key: 'inspection_expiry', label: 'ITP' },
                      { key: 'casco_expiry', label: 'CASCO' }
                    ].map(({ key, label }) => {
                      const pendingDoc = pendingDocuments[car.id]?.find(
                        doc => doc.field_name === key && doc.status === 'pending'
                      );
                      
                      return (
                        <TableCell key={key}>
                          <div className="flex flex-col gap-1">
                            {car[key] ? (
                              (() => {
                                // Normalizăm datele pentru comparație corectă
                                const expiryDate = new Date(car[key]);
                                expiryDate.setHours(0, 0, 0, 0);
                                const today = new Date();
                                today.setHours(0, 0, 0, 0);
                                // Expirat = ziua de azi e DUPĂ data expirării
                                const isExpired = today > expiryDate;
                                
                                return (
                                  <span 
                                    onClick={() => handleShowHistory(car, key, label)}
                                    className={`text-xs cursor-pointer hover:underline ${isExpired ? 'text-red-600 font-semibold' : 'text-slate-600'}`}
                                    data-testid={`${key.replace('_', '-')}-history-${car.id}`}
                                  >
                                    {new Date(car[key]).toLocaleDateString()}
                                  </span>
                                );
                              })()
                            ) : '-'}
                            
                            {/* Badge pentru document viitor */}
                            {pendingDoc && (
                              <div className="flex flex-col gap-1 mt-1">
                                <span 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (pendingDoc.document && handleViewPendingDocument) {
                                      handleViewPendingDocument(pendingDoc, car.name, car.license_plate);
                                    }
                                  }}
                                  className={`text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${pendingDoc.document ? 'cursor-pointer hover:bg-green-200' : ''}`}
                                  title={pendingDoc.document ? 'Click pentru a vedea documentul viitor' : 'Document programat fără fișier atașat'}
                                >
                                  📄 {new Date(pendingDoc.activation_date).toLocaleDateString('ro-RO', { day: 'numeric', month: 'short' })}
                                  {pendingDoc.document && <Eye className="w-3 h-3 ml-1" />}
                                </span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCancelPendingDocument(pendingDoc.id, car.id);
                                  }}
                                  className="text-[10px] text-red-600 hover:underline"
                                  title="Anulează document viitor"
                                >
                                  Anulează
                                </button>
                              </div>
                            )}
                          </div>
                        </TableCell>
                      );
                    })}
                    
                    {/* Coloana Copie Conformă - cu suport pentru "Nenecesar" */}
                    <TableCell>
                      {car.copie_conforma_nenecesar ? (
                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                          Nenecesar
                        </span>
                      ) : (
                        <div className="flex flex-col gap-1">
                          {car.copie_conforma_expiry ? (
                            (() => {
                              const expiryDate = new Date(car.copie_conforma_expiry);
                              expiryDate.setHours(0, 0, 0, 0);
                              const today = new Date();
                              today.setHours(0, 0, 0, 0);
                              const isExpired = today > expiryDate;
                              
                              return (
                                <span 
                                  onClick={() => handleShowHistory(car, 'copie_conforma_expiry', 'Copie Conformă')}
                                  className={`text-xs cursor-pointer hover:underline ${isExpired ? 'text-red-600 font-semibold' : 'text-slate-600'}`}
                                  data-testid={`copie-conforma-expiry-history-${car.id}`}
                                >
                                  {new Date(car.copie_conforma_expiry).toLocaleDateString()}
                                </span>
                              );
                            })()
                          ) : '-'}
                          
                          {/* Badge pentru document viitor */}
                          {(() => {
                            const pendingDoc = pendingDocuments[car.id]?.find(
                              doc => doc.field_name === 'copie_conforma_expiry' && doc.status === 'pending'
                            );
                            return pendingDoc ? (
                              <div className="flex flex-col gap-1 mt-1">
                                <span 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (pendingDoc.document && handleViewPendingDocument) {
                                      handleViewPendingDocument(pendingDoc, car.name, car.license_plate);
                                    }
                                  }}
                                  className={`text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${pendingDoc.document ? 'cursor-pointer hover:bg-green-200' : ''}`}
                                  title={pendingDoc.document ? 'Click pentru a vedea documentul viitor' : 'Document programat fără fișier atașat'}
                                >
                                  📄 {new Date(pendingDoc.activation_date).toLocaleDateString('ro-RO', { day: 'numeric', month: 'short' })}
                                  {pendingDoc.document && <Eye className="w-3 h-3 ml-1" />}
                                </span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCancelPendingDocument(pendingDoc.id, car.id);
                                  }}
                                  className="text-[10px] text-red-600 hover:underline"
                                  title="Anulează document viitor"
                                >
                                  Anulează
                                </button>
                              </div>
                            ) : null;
                          })()}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">{car.created_by || '-'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {hasPermission('masini.editare') && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditCar(car)}
                            data-testid={`edit-car-${car.id}`}
                          >
                            <Edit className="w-4 h-4 text-blue-500" />
                          </Button>
                        )}
                        {hasPermission('masini.stergere') && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteCar(car.id)}
                            data-testid={`delete-car-${car.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
});

export default MasiniTab;
